import {
  Box,
  Button,
  Stack,
  Typography,
  Divider,
  Paper,
  TextField,
  Alert,
  CircularProgress,
  Chip,
  Tooltip,
} from "@mui/material";
import { useEffect, useState } from "react";
import Filter from "../Filter";
import {
  AUDIENCE_GET_ENDPOINT,
  AUDIENCE_SEGMENT_CREATE_ENDPOINT,
  TAG_LIST_ENDPOINT,
} from "../../config/urls";
import AudienceTableComponent from "../Audience/AudienceTableComponent";
import { useAuth } from "../../hooks/AuthProvider";
import AudienceFilterChip from "../Audience/AudienceFilterChip";
import { ArrowBack } from "@mui/icons-material";
import ButtonTwo from "../../common/dashboard-button";
import ButtonOne from "../../common/normal-button";
import BackButton from "../../common/back-button";

const DEFAULT_COLUMNS = [
  { id: "name", label: "Name" },
  { id: "phone_no", label: "Phone" },
  { id: "email", label: "Email" },
  { id: "language", label: "Language" },
  { id: "gender", label: "Gender" },
  { id: "dob", label: "Date of Birth" },
  { id: "city", label: "City" },
  { id: "state", label: "State" },
  { id: "tags", label: "Tags" },
  { id: "opt_in", label: "Opted In" },
  { id: "updated_by", label: "Updated By" },
  { id: "last_updated", label: "Last Updated" },
];

const CreateSegment = ({ onNavigate }) => {
  const [segmentName, setSegmentName] = useState("");
  const [allTagOptions, setAllTagOptions] = useState([]);
  const [appliedFilters, setAppliedFilters] = useState([]);
  const [audienceCount, setAudienceCount] = useState(0);
  const [audienceData, setAudienceData] = useState([]); // audience list
  const [loading, setLoading] = useState(false);
  const [errorMsg, setErrorMsg] = useState("");
  const [successMsg, setSuccessMsg] = useState("");
  const [saving, setSaving] = useState(false);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(20);

  const { getValidToken } = useAuth();

  const filterFields = [
    { label: "Name", type: "text" },
    { label: "Phone", type: "phone" },
    { label: "Email", type: "text" },
    { label: "Language", type: "text" },
    { label: "City", type: "text" },
    { label: "State", type: "text" },
    {
      label: "Tags",
      type: "list",
      values: allTagOptions.map((tag) => tag.name),
    },
    { label: "Marketing Opt In", type: "boolean", display: ["Yes", "No"] },
    { label: "Gender", type: "single_select_list", values: ["Male", "Female", "N/A"] },
    { label: "DOB", type: "date" },
    { label: "Created At", type: "date" },
    { label: "Last Updated", type: "date" },

  ];

  const fetchTagOptions = async () => {
    const url = TAG_LIST_ENDPOINT;
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    try {
      const response = await fetch(url, { headers });
      if (!response.ok) throw new Error("Failed to fetch data");
      const data = await response.json();
      setAllTagOptions(data);
    } catch (error) {
      console.error("Error fetching tag options:", error);
    }
  };

  const fetchAudienceCountAndData = async (pageNo = 0, pageSize = 20) => {
    setLoading(true);
    const url = AUDIENCE_GET_ENDPOINT;
    const token = await getValidToken();
    const tenant = localStorage.getItem("tenant");

    const headers = {
      Authorization: `Bearer ${token}`,
      "Content-Type": "application/json",
      "X-Tenant-Id": tenant,
    };

    let queryParams = new URLSearchParams({
      limit: pageSize,
      offset: pageNo * pageSize,
    });

    appliedFilters.forEach(({ field, operator, value }) => {
      field = field.toLowerCase().replaceAll(" ", "_");
      operator = operator.toLowerCase().replaceAll(" ", "_");
      queryParams.append(`${field}`, `${operator}:${value ?? ""}`);
    });

    try {
      const response = await fetch(`${url}?${queryParams}`, { headers });
      if (!response.ok) throw new Error("Failed to fetch audience data");
      const data = await response.json();
      setAudienceData(data.results || []);
      setAudienceCount(data.count || 0);
    } catch (err) {
      console.error(err);
      setAudienceData([]);
      setErrorMsg("Failed to fetch audience data.");
    } finally {
      setLoading(false);
    }
  };

  const saveSegment = async () => {
    if (saving) return;
    if (!segmentName.trim()) {
      setErrorMsg("Please enter a segment name before saving.");
      return;
    }
    if (appliedFilters.length === 0) {
      setErrorMsg("Please apply at least one filter before saving.");
      return;
    }

    setSaving(true);
    setErrorMsg("");
    setSuccessMsg("");

    const segmentData = { name: segmentName.trim(), filters: appliedFilters };

    try {
      const token = await getValidToken();
      const tenant = localStorage.getItem("tenant");

      const response = await fetch(AUDIENCE_SEGMENT_CREATE_ENDPOINT, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
          "Content-Type": "application/json",
          "X-Tenant-Id": tenant,
        },
        body: JSON.stringify(segmentData),
      });

      if (!response.ok) {
        const errorData = await response.json();

        if (typeof errorData === "object") {
          let _errorMsg = "";

          for (const key in errorData) {
            _errorMsg += errorData[key] + "\n";
          }

          setErrorMsg(_errorMsg);
        } else {
          setErrorMsg(errorData || "Failed to save segment.");
        }
        return;
      }

      setSegmentName("");
      setAppliedFilters([]);
      setAudienceData([]);
      setAudienceCount(0);
      setErrorMsg("");
      setSuccessMsg("Segment created successfully");

      setTimeout(() => {
        setSuccessMsg("");
        onNavigate("audience", { tab: 1 });
      }, 1500);
    } catch (err) {
      console.error(err);
      setErrorMsg("Something went wrong. Please try again.");
    } finally {
      setSaving(false);
    }
  };

  const handleFilterChange = (filters) => {
    setAppliedFilters(filters);
    setPage(0);
  };

  useEffect(() => {
    fetchTagOptions();
  }, []);

  useEffect(() => {
    if (appliedFilters.length > 0) {
      fetchAudienceCountAndData(page, rowsPerPage);
    } else {
      setAudienceData([]);
      setAudienceCount(0);
    }
  }, [appliedFilters, page, rowsPerPage]);

  return (
    <Box sx={{ px: { sm: 3, xs: 1 }, mt: 2 }}>
      <BackButton
        onClick={() => {
          onNavigate("audience", { tab: 1 });
        }}
      />

      <h2 className="heading-title">Create Segment</h2>

      <Box
        sx={{
          display: "flex",
          flexDirection: "column",
          alignItems: "flex-start",
          gap: 2,
        }}
      >
        <Stack
          direction="row"
          // spacing={2}
          sx={{
            alignItems: "flex-start",
            justifyContent: "space-between",
            width: "100%",
            flexDirection: { xs: "column", sm: "row" },
            gap: 2,
          }}
        >
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              // mb: { sm: 0, xs: 1 },
            }}
          >
            <Filter fields={filterFields} onApply={handleFilterChange} />
            {appliedFilters.length > 0 && (
              <Stack direction="row" gap={1} flexWrap="wrap">
                {appliedFilters.map((f, i) => (
                  <AudienceFilterChip key={i} filter={f} />
                ))}
              </Stack>
            )}
          </Box>
          <Box
            sx={{
              display: "flex",
              alignItems: "flex-start",
              gap: 2,
              flexDirection: { xs: "column", sm: "row" },
              margin: 0,
            }}
          >
            <TextField
              label="Segment Name"
              placeholder="Enter a name for your segment"
              variant="outlined"
              size="small"
              value={segmentName}
              onChange={(e) => setSegmentName(e.target.value)}
              sx={{ flex: 1 }}
            />
            <Tooltip
              title={
                audienceCount === 0
                  ? "Cannot create segment with zero audience"
                  : ""
              }
              arrow
              placement="top"
              // open={audienceCount === 0}
            >
              <ButtonOne
                variant="contained"
                onClick={saveSegment}
                endIcon={
                  saving && <CircularProgress size={18} color="inherit" />
                }
                disabled={audienceCount === 0}
              >
                Save Segment
              </ButtonOne>
            </Tooltip>
          </Box>
        </Stack>

        {errorMsg && (
          <Alert
            sx={{ width: "100%" }}
            severity="error"
            onClose={() => setErrorMsg("")}
          >
            {errorMsg}
          </Alert>
        )}
        {successMsg && (
          <Alert
            sx={{ width: "100%" }}
            severity="success"
            onClose={() => setSuccessMsg("")}
          >
            {successMsg}
          </Alert>
        )}

        {/* Audience Table */}
        <AudienceTableComponent
          columns={DEFAULT_COLUMNS}
          audienceData={audienceData}
          rowCount={audienceCount}
          controller={{ page, rowsPerPage }}
          tenant="" // can pass tenant if needed
          tenantSpecificColumns={{}}
          handleSelectAll={() => {}}
          handleSelectRecord={() => {}}
          isRecordSelected={() => false}
          setSelectedEditAudience={() => {}}
          setEditAudienceModalOpen={() => {}}
          handleDeleteAudienceClick={() => {}}
          handlePageChange={(e, newPage) => setPage(newPage)}
          handleChangeRowsPerPage={(e) => {
            setRowsPerPage(parseInt(e.target.value, 10));
            setPage(0);
          }}
        />
      </Box>
    </Box>
  );
};

export default CreateSegment;
